'use client';

import { TextPassage } from '@petsmart-ui/sparky';
import { useRef } from 'react';
import { A11y } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperRef } from 'swiper/types';

import { hoveredImageGalleryInstructions, imageGalleryInstructions } from '@libs/common-next';

import type { MediaEntityType } from '@/entities/product';

import { A11Y_OPTIONS } from '../../lib';
import { useGalleryStore } from '../../model';
import { GalleryPagination } from '../GalleryPagination';
import { ImageSlide } from '../ImageSlide';
import { MoreButton } from '../MoreButton';
import { VideoSlide } from '../VideoSlide';

import 'react-inner-image-zoom/lib/styles.min.css';
import 'swiper/css';
import 'swiper/css/a11y';
import './GalleryBase.scss';

export type GalleryBaseProps = Readonly<{
    media: MediaEntityType[];
    productName: string;
}>;

export const GalleryBase = ({ media, productName }: GalleryBaseProps) => {
    const openModal = useGalleryStore(state => state.openModal);
    const swiperRef = useRef<SwiperRef | null>(null);

    const handleSwiperReady = (swiper: SwiperRef) => {
        swiperRef.current = swiper;
    };

    const handleClick = (swiper: SwiperRef) => {
        const currentMedia = media[swiper.activeIndex];
        if (currentMedia.type === 'video') return;

        openModal(swiper.activeIndex);
    };

    const handleBulletClick = (index: number) => {
        swiperRef.current?.slideTo(index);
    };

    return (
        <div data-testid="test-gallery" id="gallery" className="pdp-gallery">
            <Swiper
                spaceBetween={10}
                slidesPerView={1}
                modules={[A11y]}
                a11y={A11Y_OPTIONS}
                onSwiper={handleSwiperReady}
                onClick={handleClick}
            >
                {media.map((item, index) => {
                    return (
                        <SwiperSlide key={index}>
                            {item.type === 'image' && (
                                <ImageSlide item={item} productName={productName} index={index} />
                            )}

                            {item.type === 'video' && <VideoSlide video={item} index={index} />}
                        </SwiperSlide>
                    );
                })}

                <TextPassage align="center" className="pdp-gallery__zoom-instruction">
                    <span className="pdp-gallery__zoom-instruction-text pdp-gallery__zoom-instruction-text--default">
                        {imageGalleryInstructions}
                    </span>
                    <span className="pdp-gallery__zoom-instruction-text pdp-gallery__zoom-instruction-text--hover">
                        {hoveredImageGalleryInstructions}
                    </span>
                </TextPassage>
            </Swiper>

            <div className="pdp-gallery__pagination" data-testid="gallery-base-pagination">
                <GalleryPagination media={media} productName={productName} onBulletClick={handleBulletClick} />

                <MoreButton media={media} productName={productName} />
            </div>
        </div>
    );
};
